import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    Task,
    getEntityLockedTooltip,
    isEntityLockedTaskType,
} from "@common/types/task";
import { memo, useMemo } from "react";
import { useUpdateTask } from "@api/task.api";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useEntities } from "@hooks/useEntities";

import { NestedTaskRow } from "../../types";

export const EntitiesCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        NestedTaskRow,
        number[] | null | undefined
    >) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);
        const entities = useEntities();

        const selectedEntities = useMemo(
            () =>
                entities.filter((entity) =>
                    task.entityIds?.includes(entity.id),
                ),
            [entities, task.entityIds],
        );

        if (row.original.isGroup || row.original.isNewPlaceholder) {
            return null;
        }

        if (isEntityLockedTaskType(task.type)) {
            const lockedTooltip = getEntityLockedTooltip(task.type);
            return (
                <ViewTableCell>
                    <EditableCellContent
                        isEmpty={!task.entityIds?.length}
                        hideIcon
                        addText="Add entities"
                        readonly
                        tooltip={lockedTooltip}
                    >
                        {task.entityIds?.length ? (
                            <EntityAvatarGroup
                                entities={selectedEntities}
                                max={3}
                                size="xs"
                            />
                        ) : null}
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <EntityFilterCombobox
                    filters={{ entityIds: task.entityIds ?? [] }}
                    onChange={({ entityIds }) =>
                        updateTask.mutate({ entityIds: entityIds ?? [] })
                    }
                    mode="selection"
                    renderTrigger={({
                        triggerProps,
                        selectedEntities: triggerEntities,
                    }) => (
                        <EditableCellContent
                            isEmpty={!task.entityIds?.length}
                            hideIcon
                            addText="Add entities"
                            triggerProps={triggerProps}
                        >
                            {triggerEntities.length > 0 && (
                                <EntityAvatarGroup
                                    entities={triggerEntities}
                                    max={3}
                                    size="xs"
                                />
                            )}
                        </EditableCellContent>
                    )}
                />
            </ViewTableCell>
        );
    },
);

EntitiesCell.displayName = "EntitiesCell";
